import { useCallback, useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { UserIdSearch } from '../../../components/UserIdSearch';
import { useAuth } from '../../../hooks/useAuth';
import type { ObsessLessUser } from '../../../types/commonTypes';
import { journeyService } from '../services/journeyService';
import type {
    JourneyChapterStatus,
    JourneyChapterTemplateFirestore,
    JourneyItemStatus,
    JourneySectionStatus,
    JourneyState,
    UserJourneyTree,
} from '../services/journeyTypes';
import { JourneyProgressManager } from './JourneyProgressManager';
import { JourneyStateEditor } from './userProgress/JourneyStateEditor';
import { UserProgressTree } from './userProgress/UserProgressTree';
import { UserLinks } from '../../../components/UserLinks';

interface SelectedUser {
    uid: string;
    label: string;
}

export const JourneyUserProgress = () => {
    const { user } = useAuth();

    const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
    const [chapterTemplates, setChapterTemplates] = useState<JourneyChapterTemplateFirestore[]>([]);
    const [journeyState, setJourneyState] = useState<JourneyState | null>(null);
    const [tree, setTree] = useState<UserJourneyTree>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Default to the logged-in admin.
    useEffect(() => {
        if (!selectedUser && user) {
            setSelectedUser({ uid: user.uid, label: user.email ?? user.displayName ?? user.uid });
        }
    }, [user, selectedUser]);

    const loadProgress = useCallback(async (uid: string) => {
        setIsLoading(true);
        try {
            const [templates, state, userTree] = await Promise.all([
                journeyService.getChapters(),
                journeyService.getUserJourneyState(uid),
                journeyService.getUserJourneyTree(uid),
            ]);
            setChapterTemplates(templates);
            setJourneyState(state);
            setTree(userTree);
        } catch (error) {
            console.error('Failed to load user journey progress:', error);
            alert('Failed to load user journey progress. Check the console for details.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedUser) {
            void loadProgress(selectedUser.uid);
        }
    }, [selectedUser, loadProgress]);

    const refresh = useCallback(async () => {
        if (selectedUser) await loadProgress(selectedUser.uid);
    }, [selectedUser, loadProgress]);

    const handleSearchResult = async (found: ObsessLessUser | null) => {
        if (!found) return;
        setSelectedUser({ uid: found.uid, label: found.email ?? found.displayName ?? found.uid });
    };

    const handleBackToMe = () => {
        if (user) {
            setSelectedUser({ uid: user.uid, label: user.email ?? user.displayName ?? user.uid });
        }
    };

    const handleStateSave = async (patch: Partial<JourneyState>) => {
        if (!selectedUser) return;
        try {
            await journeyService.updateUserJourneyState(selectedUser.uid, patch);
            await refresh();
        } catch (error) {
            console.error('Failed to update journey state:', error);
            alert('Failed to update journey state. Check the console for details.');
        }
    };

    const handleChapterStatusChange = async (chapterId: string, status: JourneyChapterStatus) => {
        if (!selectedUser) return;
        await journeyService.updateUserChapterStatus(selectedUser.uid, chapterId, status);
        await refresh();
    };

    const handleSectionStatusChange = async (chapterId: string, sectionId: string, status: JourneySectionStatus) => {
        if (!selectedUser) return;
        await journeyService.updateUserSectionStatus(selectedUser.uid, chapterId, sectionId, status);
        await refresh();
    };

    const handleItemChange = async (
        chapterId: string,
        sectionId: string,
        itemId: string,
        patch: { status?: JourneyItemStatus; progress?: number },
    ) => {
        if (!selectedUser) return;
        await journeyService.updateUserItemProgress(selectedUser.uid, chapterId, sectionId, itemId, patch);
        await refresh();
    };

    const handleChatReset = async (
        chapterId: string,
        sectionId: string,
        itemId: string,
        options: { clearOcdProfile: boolean },
    ) => {
        if (!selectedUser) return;
        try {
            await journeyService.resetUserChatItem(selectedUser.uid, chapterId, sectionId, itemId, options);
            await refresh();
        } catch (error) {
            console.error('Failed to reset chat quest:', error);
            alert('Failed to reset chat quest. Check the console for details.');
        }
    };

    const isViewingSelf = !!user && selectedUser?.uid === user.uid;

    return (
        <div>
            <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <p className="text-xs font-medium text-gray-500">Viewing progress for</p>
                        <p className="font-semibold text-gray-900">{selectedUser?.label ?? '—'}</p>
                        <p className="font-mono text-xs text-gray-500 break-all">{selectedUser?.uid}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {selectedUser?.uid && <UserLinks userId={selectedUser.uid} />}
                        {!isViewingSelf && (
                            <Button variant="outline" size="sm" onClick={handleBackToMe}>
                                Back to my journey
                            </Button>
                        )}
                    </div>
                </div>
                <UserIdSearch onSearchResult={handleSearchResult} />
            </div>

            {isLoading && <p className="text-gray-500">Loading progress...</p>}

            {!isLoading && selectedUser && (
                <>
                    {journeyState ? (
                        <JourneyStateEditor journeyState={journeyState} onSave={handleStateSave} />
                    ) : (
                        <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
                            This user has no journey state yet. Use the jump tool below to initialize their journey.
                        </div>
                    )}

                    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
                        <JourneyProgressManager
                            userId={selectedUser.uid}
                            chapterTemplates={chapterTemplates}
                            onJourneyUpdate={refresh}
                        />
                    </div>

                    {tree.length > 0 ? (
                        <UserProgressTree
                            tree={tree}
                            userId={selectedUser.uid}
                            onChapterStatusChange={handleChapterStatusChange}
                            onSectionStatusChange={handleSectionStatusChange}
                            onItemChange={handleItemChange}
                            onChatReset={handleChatReset}
                        />
                    ) : (
                        <p className="text-sm text-gray-500">
                            No compiled chapters for this user yet. Use the jump tool above to compile and position their journey.
                        </p>
                    )}
                </>
            )}
        </div>
    );
};
